import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/bs/Tooltip';
import {GRAY_DARK, RED_LIGHT} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {Optional} from 'util/Types';

export interface OptionValue {
  text: string;
  value?: string | null;
}

interface DropdownValueTooltipProps {
  tooltipText?: string;
}

const DropdownValueTooltip = (props: DropdownValueTooltipProps): JSX.Element | null => {
  if (props.tooltipText) {
    return (
      <>
        <Tooltip title={props.tooltipText}>
          <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
        </Tooltip>
      </>
    );
  } else {
    return null;
  }
};

interface DropdownValueFormContentsProps {
  disabled?: boolean;
  error?: Optional<string | JSX.Element>;
  isInline?: boolean;
  options: Array<OptionValue>;
  onUpdate: (value?: string) => void;
  isRequired?: boolean;
  value?: unknown;
  label: string;
  dataTestAttrs?: DataTestAttrs;
  selectionPlaceholder?: string;
  tooltipText?: string;
}

const DropdownValueFormContents = (props: DropdownValueFormContentsProps): JSX.Element => {
  const selectedOption = props.options.find((option) => option.value === props.value);
  return (
    <div {...getTestProps({component: 'DropdownValueForm', element: 'root'})}>
      {!props.isInline ? (
        <FieldLabel label={props.label} labelStyle={LabelStyle.LABEL} isRequired={props.isRequired} />
      ) : null}
      <DropdownValueTooltip tooltipText={props.tooltipText} />
      <SelectDropdown
        buttonClassName={css(props.isInline && props.error != null && styles.hasError)}
        disabled={props.disabled}
        displayName={selectedOption ? selectedOption.text : (props.selectionPlaceholder ?? 'Select')}
        alwaysDropDown={true}
        error={!props.isInline ? props.error : null}
        menu={
          <SearchableMenu
            selectedValue={props.value}
            onSelect={(value) => {
              props.onUpdate(value);
            }}
            optionDatas={props.options.map((option) => ({
              name: option.text,
              value: option.value,
            }))}
            height={Menu.HEIGHTS.MEDIUM}
            width={props.isInline ? Menu.WIDTHS.MEDIUM : Menu.WIDTHS.FULL}
            scrollable={true}
          />
        }
        {...getTestProps(props.dataTestAttrs)}
        dataTestAttrs={{component: 'DropdownValueFormSelect', element: 'selectDropdown'}}
      />
    </div>
  );
};

export interface DropdownValueFormProps {
  disabled?: boolean;
  error?: Optional<string | JSX.Element>;
  isInline?: boolean;
  label: string;
  options: Array<OptionValue>;
  isRequired?: boolean;
  value?: unknown;
  dataTestAttrs?: DataTestAttrs;
  onUpdate: (value?: string) => void;
  selectionPlaceholder?: string;
  tooltipText?: string;
}

const DropdownValueForm = (props: DropdownValueFormProps): JSX.Element => (
  <DropdownValueFormContents
    disabled={props.disabled}
    error={props.error}
    isInline={props.isInline}
    label={props.label}
    value={props.value}
    options={props.options}
    isRequired={props.isRequired}
    onUpdate={props.onUpdate}
    dataTestAttrs={props.dataTestAttrs}
    selectionPlaceholder={props.selectionPlaceholder}
    tooltipText={props.tooltipText}
  />
);

export default DropdownValueForm;

const styles = StyleSheet.create({
  hasError: {
    border: `1px solid ${RED_LIGHT}`,
  },
  infoIcon: {
    marginLeft: '4px',
    color: GRAY_DARK,
  },
});
